import React, { useState, useEffect, useRef } from 'react';
import {Camera, Layout, Send, X, Check, MousePointer2, RefreshCw} from 'lucide-react';

interface EditorProps {
    imageUrl: string;
    onSave: (dataUrl: string) => void;
    onClose: () => void;
}

const ForensicImageEditor: React.FC<EditorProps> = ({ imageUrl, onSave, onClose }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [tool, setTool] = useState<'arrow' | 'blur'>('arrow');
    const [isDrawing, setIsDrawing] = useState(false);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [imageLoaded, setImageLoaded] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        const img = new Image();
        // crossOrigin va impostato prima di src
        img.crossOrigin = "anonymous";

        //const cacheBuster = imageUrl.includes('?') ? `&t=${Date.now()}` : `?t=${Date.now()}`;
        //img.src = imageUrl + cacheBuster;

        img.src = imageUrl;

        img.onload = () => {
            const maxWidth = 1000;
            const scale = Math.min(maxWidth / img.width, 1);
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;
            ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
            setImageLoaded(true);
        };
    }, [imageUrl]);

    const getPos = (e: React.MouseEvent) => {
        const rect = canvasRef.current!.getBoundingClientRect();
        return {
            x: (e.clientX - rect.left) * (canvasRef.current!.width / rect.width),
            y: (e.clientY - rect.top) * (canvasRef.current!.height / rect.height)
        };
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!imageLoaded) return;
        setIsDrawing(true);
        setStartPos(getPos(e));
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        if (!isDrawing) return;
        const endPos = getPos(e);
        const ctx = canvasRef.current!.getContext('2d')!;

        if (tool === 'arrow') {
            drawArrow(ctx, startPos.x, startPos.y, endPos.x, endPos.y);
        } else if (tool === 'blur') {
            applyForensicBlur(ctx, startPos.x, startPos.y, endPos.x - startPos.x, endPos.y - startPos.y);
        }
        setIsDrawing(false);
    };

    const drawArrow = (ctx: CanvasRenderingContext2D, fromX: number, fromY: number, toX: number, toY: number) => {
        const headLength = 20;
        const angle = Math.atan2(toY - fromY, toX - fromX);
        ctx.strokeStyle = "#ff0000";
        ctx.lineWidth = 5;
        ctx.lineCap = "round";
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 5;

        ctx.beginPath();
        ctx.moveTo(fromX, fromY);
        ctx.lineTo(toX, toY);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(toX, toY);
        ctx.lineTo(toX - headLength * Math.cos(angle - Math.PI / 6), toY - headLength * Math.sin(angle - Math.PI / 6));
        ctx.moveTo(toX, toY);
        ctx.lineTo(toX - headLength * Math.cos(angle + Math.PI / 6), toY - headLength * Math.sin(angle + Math.PI / 6));
        ctx.stroke();

        ctx.shadowBlur = 0; // Reset ombre
    };

    const applyForensicBlur = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) => {
        const blockSize = 8;
        const startX = w > 0 ? x : x + w;
        const startY = h > 0 ? y : y + h;
        const absW = Math.abs(w);
        const absH = Math.abs(h);

        for (let i = 0; i < absW; i += blockSize) {
            for (let j = 0; j < absH; j += blockSize) {
                // Preleva il colore del pixel centrale del blocco
                const pixelData = ctx.getImageData(startX + i, startY + j, 1, 1).data;
                ctx.fillStyle = `rgb(${pixelData[0]}, ${pixelData[1]}, ${pixelData[2]})`;
                // Disegna un quadrato di quel colore (effetto pixelato/blur investigativo)
                ctx.fillRect(startX + i, startY + j, blockSize, blockSize);
            }
        }
    };

    return (
        <div className="fixed inset-0 z-[100] bg-slate-900/95 flex flex-col items-center justify-center p-4 backdrop-blur-md" onClick={onClose}>
            <div className="bg-white rounded-2xl overflow-hidden flex flex-col max-w-3xl lg:max-w-5xl w-full shadow-2xl border border-slate-700" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b flex justify-between items-center bg-slate-50">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-600 p-2 rounded-lg text-white"><Camera size={20}/></div>
                        <div>
                            <h3 className="font-bold text-slate-800">Analisi Forense Immagine</h3>
                            <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Strumenti di evidenza e privacy</p>
                        </div>
                    </div>
                    <div className="flex bg-slate-200 p-1 rounded-xl gap-1">
                        <button onClick={() => setTool('blur')} className={`px-4 py-2 rounded-lg text-xs font-black flex items-center gap-2 transition-all ${tool === 'blur' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:bg-slate-300'}`}>
                            <Layout size={14}/> BLUR PRIVACY
                        </button>
                        <button onClick={() => setTool('arrow')} className={`px-4 py-2 rounded-lg text-xs font-black flex items-center gap-2 transition-all ${tool === 'arrow' ? 'bg-white text-red-600 shadow-sm' : 'text-slate-600 hover:bg-slate-300'}`}>
                            <Send size={14} className="rotate-[-45deg]"/> INDICA TARGET
                        </button>
                    </div>
                </div>

                <div className="relative bg-slate-200 flex justify-center items-center overflow-hidden p-8 min-h-[500px]">
                    {!imageLoaded && <div className="absolute inset-0 flex items-center justify-center bg-white/50 z-10"><RefreshCw className="animate-spin text-blue-600" size={32}/></div>}
                    <canvas
                        ref={canvasRef}
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                        className={`shadow-2xl bg-white max-w-full h-auto cursor-crosshair rounded-sm`}
                    />
                </div>

                <div className="p-4 border-t flex justify-between items-center bg-slate-50">
                    <p className="text-[11px] text-slate-500 italic">Trascina per disegnare {tool === 'blur' ? 'l\'area da oscurare' : 'la freccia rossa di indicazione'}</p>
                    <div className="flex gap-3">
                        <button onClick={onClose} className="px-6 py-2.5 text-sm font-bold text-slate-400 hover:text-slate-600 transition-colors">ANNULLA</button>
                        <button
                            onClick={() => onSave(canvasRef.current!.toDataURL("image/jpeg", 0.95))}
                            className="px-8 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-black shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2"
                        >
                            <Check size={18}/> APPLICA ALLA RELAZIONE
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForensicImageEditor;